import { without } from "../fields";
import { type AxialCoord, hexDistance, hexEquals, hexKey } from "../hex";
import { ownWorkingCity } from "../land/cities";
import { canTake } from "../land/territory";
import { applyFirst, rankBy } from "../pick";
import { findHex, unitsOf } from "../run/board";
import { onCadence } from "../run/cadence";
import { kingOf, withKing } from "../run/pools";
import { scriptUnitsOf, soldierSeats } from "../run/seats";
import { allied, sameOwner } from "../run/sides";
import { payDeed } from "../soldier/bounty";
import { enlistedWith, marchesWith, onDuty } from "../soldier/company";
import { seatLeader } from "../soldier/lead";
import type {
  FactionId,
  HexState,
  King,
  Mark,
  MarkKind,
  RunState,
  SeatState,
  Unit,
} from "../types/state";
import { orderUnit } from "../units/routes";
import { restedFor } from "../units/unit";
import { commandRefusal, spendWill } from "./king";

/**
 * The King as a general (A33-A41): the tax and the rush, the call, the banners, and the soldiers who
 * answer them.
 */

/** The faction's tax is running (A34): its supply income is `taxPercent` this interval. */
export function taxActive(state: RunState, faction: FactionId): boolean {
  const until = kingOf(state, faction)?.taxUntilTick;
  return until !== undefined && state.tick <= until;
}

/** Ticks until the tax can be raised again; 0 when it is ready. */
export function taxCooldown(state: RunState, faction: FactionId): number {
  return ticksUntil(state, kingOf(state, faction)?.taxReadyTick);
}

/** Ticks until `readyTick` comes; 0 once it has, or without one. */
function ticksUntil(state: RunState, readyTick: number | undefined): number {
  return Math.max(0, (readyTick ?? 0) - state.tick);
}

/** A grant from the throne (tax, rush, call): its Will paid and the King's record patched; the same run back without a King. */
function grant(
  state: RunState,
  faction: FactionId,
  kind: "tax" | "rush" | "call",
  patch: (king: King) => King,
): RunState {
  const paid = spendWill(state, faction, kind);
  const king = kingOf(paid, faction);
  return king ? withKing(paid, faction, patch(king)) : state;
}

/** The tax grant's precondition: a court, the throne, the Will, the cooldown over. */
export function canTax(state: RunState, faction: FactionId): boolean {
  const king = kingOf(state, faction);
  return (
    state.status === "running" &&
    king !== undefined &&
    taxCooldown(state, faction) === 0 &&
    commandRefusal(state, faction, king.coord, "tax") === undefined
  );
}

/** The King raises the tax from his throne (A34). Same object back when refused. */
export function raiseTax(state: RunState, faction: FactionId): RunState {
  const general = state.rules.court?.general;
  if (!general || !canTax(state, faction)) {
    return state;
  }
  return grant(state, faction, "tax", (king) => ({
    ...king,
    taxUntilTick: state.tick + general.taxTicks,
    taxReadyTick: state.tick + general.taxCooldownTicks,
  }));
}

/** This city of the faction's trains under the King's rush (A34). */
export function isRushed(state: RunState, faction: FactionId, coord: AxialCoord): boolean {
  const rush = kingOf(state, faction)?.rush;
  return rush !== undefined && state.tick <= rush.untilTick && hexEquals(rush.city, coord);
}

/** Ticks until the next rush can be granted; 0 when it is ready. */
export function rushCooldown(state: RunState, faction: FactionId): number {
  return ticksUntil(state, kingOf(state, faction)?.rushReadyTick);
}

/** The rush grant's precondition: an own working city, the throne, the Will, the cooldown over. */
export function canRush(state: RunState, faction: FactionId, city: AxialCoord): boolean {
  return (
    state.status === "running" &&
    state.kings !== null &&
    ownWorkingCity(state, faction, city) !== undefined &&
    rushCooldown(state, faction) === 0 &&
    commandRefusal(state, faction, city, "rush") === undefined
  );
}

/** The King opens his purse to one city from his throne (A34). Same object back when refused. */
export function rushCity(state: RunState, faction: FactionId, city: AxialCoord): RunState {
  const general = state.rules.court?.general;
  if (!general || !canRush(state, faction, city)) {
    return state;
  }
  return grant(state, faction, "rush", (king) => ({
    ...king,
    rush: { city, untilTick: state.tick + general.rushTicks },
    rushReadyTick: state.tick + general.rushCooldownTicks,
  }));
}

/** The capture call's precondition: the throne and the Will. */
export function canCall(state: RunState, faction: FactionId): boolean {
  const king = kingOf(state, faction);
  return (
    state.status === "running" &&
    king !== undefined &&
    commandRefusal(state, faction, king.coord, "call") === undefined
  );
}

/**
 * The King calls the whole kingdom from his throne to capture land (A33). The standing call
 * again is a no-op; same object back when refused.
 */
export function setCall(state: RunState, faction: FactionId): RunState {
  if (kingOf(state, faction)?.call || !canCall(state, faction)) {
    return state;
  }
  return grant(state, faction, "call", (king) => ({ ...king, call: { kind: "capture" } }));
}

/** The King takes his call back, from anywhere and for no Will. Same object back without one. */
export function clearCall(state: RunState, faction: FactionId): RunState {
  const king = kingOf(state, faction);
  if (!king?.call) {
    return state;
  }
  const silent = without(king, "call");
  return withKing(state, faction, silent);
}

/** The banner a hex can carry (A39): attack on foreign land, defend on own, none on razed land. */
export function markKindAt(
  state: RunState,
  faction: FactionId,
  target: AxialCoord,
): MarkKind | undefined {
  const hex = findHex(state, target);
  if (!hex || hex.razed) {
    return undefined;
  }
  return hex.owner === faction ? "defend" : "attack";
}

/** The faction's banners, the most urgent first. */
export function marksOf(state: RunState, faction: FactionId): readonly Mark[] {
  return kingOf(state, faction)?.marks ?? [];
}

function withMarks(state: RunState, faction: FactionId, marks: readonly Mark[]): RunState {
  const king = kingOf(state, faction);
  if (!king) {
    return state;
  }
  const bare = without(king, "marks");
  return withKing(state, faction, marks.length > 0 ? { ...bare, marks } : bare);
}

/**
 * The mark verb's precondition: land that can carry a banner and has none, in the banner's
 * range, and the Will. A muster point goes with an attack banner only and is own land.
 */
export function canMark(
  state: RunState,
  faction: FactionId,
  target: AxialCoord,
  from?: AxialCoord,
): boolean {
  const musterFits =
    from === undefined ||
    (markKindAt(state, faction, target) === "attack" &&
      markKindAt(state, faction, from) === "defend");
  return (
    state.status === "running" &&
    state.kings !== null &&
    musterFits &&
    markKindAt(state, faction, target) !== undefined &&
    !marksOf(state, faction).some((mark) => hexEquals(mark.target, target)) &&
    commandRefusal(state, faction, target, "mark") === undefined
  );
}

/**
 * The King plants a banner (A39), from anywhere within `markRange` of himself. The hex decides
 * its kind. The newest banner is the most urgent: it takes the first place, the others move
 * down and the one past `markSlots` falls off. Same object back when refused.
 */
export function setMark(
  state: RunState,
  faction: FactionId,
  target: AxialCoord,
  from?: AxialCoord,
): RunState {
  const general = state.rules.court?.general;
  const kind = markKindAt(state, faction, target);
  if (!general || !kind || !canMark(state, faction, target, from)) {
    return state;
  }
  const paid = spendWill(state, faction, "mark");
  const planted: Mark = from ? { kind, target, from } : { kind, target };
  const marks = [planted, ...marksOf(paid, faction)].slice(0, general.markSlots);
  return withMarks(paid, faction, marks);
}

/** The King takes a banner down, from anywhere and for no Will. Same object back without one there. */
export function clearMark(state: RunState, faction: FactionId, target: AxialCoord): RunState {
  const marks = marksOf(state, faction);
  const kept = marks.filter((mark) => !hexEquals(mark.target, target));
  return kept.length === marks.length ? state : withMarks(state, faction, kept);
}

/** Whether the seat's soldier stands in the banner's ring, at work and unbroken (C1). */
function seatInRing(state: RunState, seat: SeatState, mark: Mark): boolean {
  const unit = onDuty(state, seat);
  const radius = state.rules.court?.general.callRadius ?? 0;
  return unit !== undefined && hexDistance(unit.coord, mark.target) <= radius;
}

/**
 * Whether the squad the seat's soldier marches with stands in the banner's ring (A68): its
 * commander or one of his squad, while the soldier is within `squadRing` of the commander.
 */
function squadInRing(state: RunState, seat: SeatState, mark: Mark): boolean {
  // VC: a leader's own squad stands in for the one it would have fallen in with.
  const leader = enlistedWith(state, seat) ?? seatLeader(state, seat);
  if (!state.rules.company?.squadBanner || !leader || !marchesWith(state, seat, leader)) {
    return false;
  }
  const radius = state.rules.court?.general.callRadius ?? 0;
  return state.units.some(
    (unit) =>
      (unit.id === leader.id || unit.squadOf === leader.id) &&
      hexDistance(unit.coord, mark.target) <= radius,
  );
}

/**
 * Every tick: a banner is answered once its attack hex is the faction's, or lost with the hex
 * it defended. An attack banner of a King that is met pays each soldier seat of his kingdom, in
 * id order, whose soldier stands in the banner's ring (C1), or, out of the ring, marches with the
 * squad that stands there (A68); a banner lost pays nobody.
 */
export function answerMarks(state: RunState): RunState {
  let next = state;
  for (const faction of state.factions) {
    const marks = marksOf(state, faction);
    const standing = marks.filter((mark) => markKindAt(state, faction, mark.target) === mark.kind);
    if (standing.length === marks.length) {
      continue;
    }
    next = withMarks(next, faction, standing);
    if (!state.rules.bounty) {
      continue;
    }
    for (const seat of soldierSeats(state)) {
      if (!sameOwner(seat.faction, faction)) {
        continue;
      }
      for (const mark of marks) {
        const met = mark.kind === "attack" && markKindAt(state, faction, mark.target) === "defend";
        if (met && seatInRing(state, seat, mark)) {
          next = payDeed(next, seat.id, "banner");
        } else if (met && squadInRing(state, seat, mark)) {
          next = payDeed(next, seat.id, "squadBanner");
        }
      }
    }
  }
  return next;
}

/**
 * Where one soldier may go, best first; empty when it is where it should be. It answers the
 * banner nearest to it (then the more urgent one), and after that the capture call.
 */
function callGoals(state: RunState, faction: FactionId, unit: Unit): AxialCoord[] {
  const radius = state.rules.court?.general.callRadius ?? 0;
  const taken = new Set<string>();
  for (const other of unitsOf(state, faction)) {
    if (other.id !== unit.id) {
      taken.add(hexKey(other.destination ?? other.coord));
    }
  }
  const free = state.hexes.filter((hex) => !hex.razed && !taken.has(hexKey(hex.coord)));
  const fromUnit = (hex: HexState): number => hexDistance(hex.coord, unit.coord);
  const goals: AxialCoord[] = [];
  // The banner nearest the soldier, then the more urgent (the earlier in the list).
  const [mark] = rankBy(marksOf(state, faction), (candidate) =>
    hexDistance(candidate.target, unit.coord),
  );
  if (mark) {
    if (mark.kind === "defend" && hexDistance(unit.coord, mark.target) <= radius) {
      return [];
    }
    const ring = free.filter(
      (hex) =>
        hexDistance(hex.coord, mark.target) <= radius &&
        (mark.kind === "defend" ? hex.owner === faction : !allied(state.rules, hex.owner, faction)),
    );
    goals.push(
      ...rankBy(ring, (hex) => hexDistance(hex.coord, mark.target), fromUnit).map(
        (hex) => hex.coord,
      ),
    );
  }
  if (kingOf(state, faction)?.call) {
    const open = free.filter((hex) => canTake(state, faction, hex.coord));
    const neutral = open.filter((hex) => hex.owner === "none");
    goals.push(...rankBy(neutral.length > 0 ? neutral : open, fromUnit).map((hex) => hex.coord));
  }
  return goals;
}

/**
 * The kingdom answers its King's banners and his call (A33, A39). Only a free soldier that has
 * neither moved nor been ordered for a whole cadence, and is not taking the land it stands on:
 * the King's hand and the mayors come first. Its order goes out as a delegate's, for no Will
 * and at any distance from the King.
 */
function callTurn(state: RunState, faction: FactionId): RunState {
  const general = state.rules.court?.general;
  const king = kingOf(state, faction);
  if (!general || !king || (!king.call && !king.marks)) {
    return state;
  }
  let next = state;
  for (const unit of scriptUnitsOf(state, faction)) {
    const rested = restedFor(state, unit, general.callCadenceTicks);
    const taking = canTake(state, faction, unit.coord);
    if (
      unit.commander ||
      unit.squadOf !== undefined ||
      unit.governs ||
      unit.destination ||
      !rested ||
      taking
    ) {
      continue;
    }
    next = applyFirst(next, callGoals(next, faction, unit), (from, goal) =>
      orderUnit(from, faction, unit.id, goal, "soldier"),
    );
  }
  return next;
}

/** End of tick, after the mayors: on the call's cadence both kingdoms answer their Kings, in faction order. */
export function callsAct(state: RunState): RunState {
  const general = state.rules.court?.general;
  if (!general || !onCadence(state, general.callCadenceTicks)) {
    return state;
  }
  let next = state;
  for (const faction of state.factions) {
    next = callTurn(next, faction);
  }
  return next;
}
